import React, { useMemo, useState } from "react";
import ComorbidityAnalysisPanel from "./ComorbidityAnalysisPanel";
import ReadmissionDetailsPanel from "./ReadmissionDetailsPanel";
import type {
  ClinicalProgressionPanelProps,
  ClinicalProgressionTab,
  DiagnosisReadmissionDriver,
} from "./types";

const TABS: { value: ClinicalProgressionTab; label: string }[] = [
  { value: "readmission-details", label: "Predictive Readmission Risk" },
  { value: "comorbidity-analysis", label: "Comorbidity Analysis" },
];

const rowKey = (driver: DiagnosisReadmissionDriver, index: number) =>
  `${driver.diagnosis}-${index}`;

const ClinicalProgressionPanel: React.FC<ClinicalProgressionPanelProps> = ({
  drivers,
  comorbidity,
  defaultTab = "readmission-details",
  onEvidenceClick,
}) => {
  const [activeTab, setActiveTab] =
    useState<ClinicalProgressionTab>(defaultTab);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const sortedDrivers = useMemo(
    () =>
      [...drivers].sort(
        (a, b) => b.readmissionDriverScore - a.readmissionDriverScore
      ),
    [drivers]
  );

  const allExpanded =
    sortedDrivers.length > 0 &&
    sortedDrivers.every((driver, index) =>
      expandedKeys.has(rowKey(driver, index))
    );

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedKeys(new Set());
      return;
    }
    setExpandedKeys(
      new Set(sortedDrivers.map((driver, index) => rowKey(driver, index)))
    );
  };

  const toggleRow = (key: string) => {
    setExpandedKeys((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const showExpandAll =
    activeTab === "readmission-details" && sortedDrivers.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <div className="flex min-h-0 flex-1 flex-col px-4 py-3">
        <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
          <div className="flex max-w-full flex-wrap gap-2">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveTab(tab.value)}
                  className={`inline-flex h-9 w-[15rem] shrink-0 items-center justify-center rounded-md border-2 px-3 text-center text-sm font-semibold whitespace-nowrap transition-colors ${
                    isActive
                      ? "border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700"
                      : "border-gray-300 bg-white text-gray-900 hover:border-gray-400 hover:bg-gray-50"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {showExpandAll ? (
            <button
              type="button"
              onClick={toggleAll}
              className="shrink-0 rounded border border-violet-200 bg-white px-2 py-0.5 text-sm font-medium text-violet-700 hover:bg-violet-50"
            >
              {allExpanded ? "Collapse All" : "Expand All"}
            </button>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {activeTab === "readmission-details" ? (
            <ReadmissionDetailsPanel
              drivers={drivers}
              onEvidenceClick={onEvidenceClick}
              expandedKeys={expandedKeys}
              onToggleRow={toggleRow}
              getRowKey={rowKey}
            />
          ) : (
            <ComorbidityAnalysisPanel data={comorbidity} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ClinicalProgressionPanel;
